import { Injectable, NgZone } from '@angular/core';
import { Router, UrlSegment } from '@angular/router';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import {MsalService} from "@azure/msal-angular";

@Injectable({
  providedIn: 'root'
})
export class UserService {
  readonly keySessionId: string = 'direct-desktop-session-id';
  readonly keyIsLoggedIn: string = 'direct-desktop-is-logged-in';
  readonly keyUserId: string = 'direct-desktop-user-id';
  readonly keyAccount: string = 'direct-desktop-account';
  readonly keyMenuItemsHierarchical: string = 'direct-desktop-menu-items-hiearchical';
  readonly keyMenuItemsFlat: string = 'direct-desktop-menu-items-flat';
  readonly keyLanguageTagTexts: string = 'languageTagTexts';
  readonly keyCompanies: string = 'direct-desktop-companies';
  readonly keyCompanyId: string = 'direct-desktop-company-id';
  readonly keyLanguages: string = 'direct-desktop-languages';
  readonly keyLanguageId: string = 'direct-desktop-language-id';

  public isAzureAuthenticated: boolean = false;

  readonly keyDart: string = 'user'; // Remove this code when Dart expires!

  private _userId: string = null;
  //private _modules: any;
  private _menuItemsFlat = null;
  private _menuItemsHierarchical = null;
  private _account = null;
  private _languageTagTexts = null;
  private _companies = null;
  private _companyId: string = null;
  private _languages = null;

  private _loginResult: any;

  private static _self: UserService = null;

  constructor(private router: Router, private zone: NgZone, private http: HttpClient, private configService: ConfigService, private authService: MsalService) {
    this.load();
    if (UserService._self == null) {
      UserService._self = this;
      setInterval(function () {
        const now = new Date();
        const nowMilliSecondsSInce1970 = now.getTime();
        //check every second to see if the session has expired (only if NOT authenticated via Azure)
        if (!UserService._self.isAzureAuthenticated
          && UserService._self.account != null
          && UserService._self.account.sessionTimeToDie_milliseconds > 0 && UserService._self.account.sessionTimeToDie_milliseconds < nowMilliSecondsSInce1970) {
          UserService._self.account.sessionTimeToDie_milliseconds = null;//reset just to be safe to avoid indefinite loop!
          localStorage.setItem("sessionExpired", "true");
          UserService._self.logout();
        }
      }, 1000);
    }
  }

  load() {
    // read from local storage
    this._userId = localStorage.getItem(this.keyUserId);
    const acnt = localStorage.getItem(this.keyAccount);
    this._account = null;
    if (typeof (acnt) !== 'undefined' && acnt !== null && acnt !== "") {
      this._account = JSON.parse(acnt);
    }
    const menuItemsHierachical = localStorage.getItem(this.keyMenuItemsHierarchical);
    if (typeof (menuItemsHierachical) !== "undefined" && menuItemsHierachical != null && menuItemsHierachical !== "") {
      this._menuItemsHierarchical = JSON.parse(menuItemsHierachical);
    }
    const menuItemsFlat = localStorage.getItem(this.keyMenuItemsFlat);
    if (typeof (menuItemsFlat) !== "undefined" && menuItemsFlat != null && menuItemsFlat !== "") {
      this._menuItemsFlat = JSON.parse(menuItemsFlat);
    }
    if (localStorage.getItem(this.keyLanguageTagTexts) !== null && localStorage.getItem(this.keyLanguageTagTexts) !== undefined && localStorage.getItem(this.keyLanguageTagTexts) !== "") {
      this._languageTagTexts = JSON.parse(localStorage.getItem(this.keyLanguageTagTexts));
    }
    if (localStorage.getItem(this.keyCompanies) !== null && localStorage.getItem(this.keyCompanies) !== undefined && localStorage.getItem(this.keyCompanies) !== "") {
      this._companies = JSON.parse(localStorage.getItem(this.keyCompanies));
    }
    this._companyId = localStorage.getItem(this.keyCompanyId);
    if (localStorage.getItem(this.keyLanguages) !== null && localStorage.getItem(this.keyLanguages) !== undefined && localStorage.getItem(this.keyLanguages) !== "") {
      this._languages = JSON.parse(localStorage.getItem(this.keyLanguages));
    }
  }

  get isLoggedIn() {
    const now = new Date();
    if (typeof (this._account) !== 'undefined' && this._account !== null && typeof (this._account.sessionExpireDate) !== 'undefined' && now > this._account.sessionExpireDate) {
      this.clearLocalStorage();
      localStorage.removeItem(this.keyIsLoggedIn);
    }
    return localStorage.getItem(this.keyIsLoggedIn) === 'true' ? true : false;
  }

  hasAccess(path: UrlSegment[]): boolean {
    let access = false;
    if (path != null && path.length == 2) {
      const accessedPath = `/${path.join("/")}`.toLowerCase();
      const firstPart = path[0].toString().toLowerCase();
      const secondPart = path[1].toString().toLowerCase();
      //allow access to the exceptions first
      if (firstPart == "external-viewer"
        || firstPart == "document" && secondPart == "document-operator-panel") {
        access = true;
      }
      else {
        //check if the accessed path is in the menu that the logged in user has access to
        if (this._menuItemsFlat != null && this._menuItemsFlat.length > 0) {
          for (let i = 0; i < this._menuItemsFlat.length; i++) {
            var mif = this._menuItemsFlat[i];
            if (mif.Route != null && mif.Route.length > 0 && mif.Route.toLowerCase() == accessedPath) {
              access = true;
              break;
            }
          }
        }
      }
    }
    else {
      //NOTE! allow all urls that are not two parts
      access = true;
    }
    return access;
  }

  get sessionId(): string {
    return localStorage.getItem(this.keySessionId) || this._loginResult?.Account.SessionId;
  }

  get userId() {
    return this._userId;
  }

  get account() {
    return this._account;
  }

  get hierarchicalMenu() {
    return this._menuItemsHierarchical;
  }

  get flatMenu() {
    return this._menuItemsFlat;
  }

  //#1685 BEA 2022-02-02
  getCurrentModuleFunctionId(): number {
    let currentModuleFunctionId: number = null;
    const currentRoute = this.router.url?.toLowerCase();
    if (this.flatMenu != null && currentRoute?.length > 0) {
      const m = this.flatMenu.filter((mm) => {
        return mm?.Route?.toLowerCase() == currentRoute;
      });
      //SHOULD be only ONE record matching the current route
      //(if not we must assume they are configured with the same ModuleFunctionId)
      if (m.length > 0) {
        currentModuleFunctionId = m[0].ModuleFunctionId;
      }
    }
    return currentModuleFunctionId;
  }

  get languageTagTexts() {
    return this._languageTagTexts;
  }

  get companies() {
    return this._companies;
  }

  get companyId() {
    return this._companyId;
  }

  get languages() {
    return this._languages;
  }

  get languageId() {
    return localStorage.getItem(this.keyLanguageId);
  }

  mustEnforcePasswordRules(): boolean {
    var value = this._account.SystemSettings._261;

    return value && value === "1" || value.toLowerCase() === "true";
  }

  getLogin(userName: string, password: string): Observable<any> {
    userName = encodeURIComponent(userName);
    password = encodeURIComponent(password);

    return this.http.get<any>(this.configService.config.directUrl + `/api/core/login?userId=${userName}&password=${password}`);
  }

  getLanguages(): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'SessionId': (this.sessionId ?? "00000000-0000-0000-0000-000000000000")
      })
    };
    return this.http.get<any>(this.configService.config.directUrl + `/api/language/GetLanguage`, httpOptions);
  }

  switchLanguage(rec: any): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    return this.http.post<any>(this.configService.config.directUrl + '/api/core/switchlanguage?SessionId=' + this.sessionId, rec, httpOptions).pipe( // Old api, session not in headers...
      tap(data => this.setDartLocalStorage(data))
    );
  }

  switchCompany(companyId: string): Observable<any> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      })
    };
    const rec = {
      "EmployeeId": this.account.EmployeeId,
      "CompanyId": companyId
    };
    return this.http.post<any>(this.configService.config.directUrl + '/api/core/switchcompany?SessionId=' + this.sessionId, rec, httpOptions); // Old api, session not in headers...
  }

  // Remove this code when Dart expires!
  private setDartLocalStorage(result: any) {
    const userdata = {
      employeeId: result.Account.EmployeeId,
      employeeNumber: result.Account.EmployeeNumber,
      employeeCompanyId: result.Account.CompanyId,
      departmentId: result.Account.DepartmentId,
      departmentName: result.Account.DepartmentName,
      name: result.Account.Name,
      firstName: result.Account.FirstName,
      surName: result.Account.SurName,
      userId: result.Account.UserId,
      companyId: result.Account.CompanyId,
      companyNumber: result.Account.CompanyNumber,
      companyName: result.Account.CompanyName,
      sessionId: result.Account.SessionId,
      languageId: result.Account.LanguageId,
      languageName: result.Account.LanguageName,
      modules: result.Modules,
      modulesAllFields: result.ModulesAllFields,
      modulefunctions: result.ModuleFunctions,
      languageTagTexts: JSON.stringify(result.LanguageTagTexts),
      companies: result.Companies,
      systemSettings: result.Account.SystemSettings,
      isSupervisor: result.Account.IsSupervisor,
    }
    localStorage.setItem(this.keyDart, JSON.stringify(userdata));
  }

  async loginData(result: any) {

    let now = new Date();
    if (result.Error === '') {
      if (result.SessionTimeToLive_milliSeconds > 0) {
        result.Account.sessionExpireDate = new Date(now.getTime() + result.SessionTimeToLive_milliSeconds);
        result.Account.sessionTimeToDie_milliseconds = result.Account.sessionExpireDate.getTime();
        //BEA 2022-07-12 harmonize the session in localstorage with direct.operation
        result.Account.sessionTimeToLive_milliSeconds = result.SessionTimeToLive_milliSeconds;
      }

      //BEA 2022-07-12 harmonize the session in localstorage with direct.operation
      result.Account.BuildVersion = result.BuildVersion;
      result.Account.MenuHierarchical_diRect_Operation = result.MenuHierarchical_diRect_Operation;
      result.Account.MenuFlat_diRect_Operation = result.MenuFlat_diRect_Operation;
      result.Account.defaultModuleId = result.DefaultModuleId;
      result.Account.workplaceId = null;
      result.Account.loginTimeStamp = now.getTime();
      result.Account.sessionTimeToLive_milliSeconds = result.SessionTimeToLive_milliSeconds;
      result.Account.operationSettings = result.OperationSettings;
      result.Account.Mode = result.Mode;

      this._account = result.Account;
      this._menuItemsFlat = result.MenuFlat_diRect_Desktop;
      this._menuItemsHierarchical = result.MenuHierarchical_diRect_Desktop;
      localStorage.setItem(this.keyIsLoggedIn, 'true');
      localStorage.setItem(this.keySessionId, result.Account.SessionId);
      localStorage.setItem(this.keyUserId, result.Account.UserId);
      localStorage.setItem(this.keyAccount, JSON.stringify(result.Account));
      localStorage.setItem(this.keyMenuItemsHierarchical, JSON.stringify(result.MenuHierarchical_diRect_Desktop));
      localStorage.setItem(this.keyMenuItemsFlat, JSON.stringify(result.MenuFlat_diRect_Desktop));
      localStorage.setItem(this.keyCompanies, JSON.stringify(result.Companies));
      localStorage.setItem(this.keyCompanyId, JSON.stringify(result.Account.CompanyId));
      localStorage.setItem(this.keyLanguages, JSON.stringify(result.Languages));
      localStorage.setItem(this.keyLanguageId, JSON.stringify(result.Account.LanguageId));
      this.setLanguageTagTexts(result.LanguageTagTexts, result.Account.LanguageId);
      this._userId = result.Account.UserId;

      this._loginResult = result;

      this.setDartLocalStorage(result); // Remove this code when Dart expires!
    }
  }

  public setLanguageTagTexts(ltt, languageId) {
    const lng = {};

    for (let i = 0; i < ltt.length; i++) {
      lng[ltt[i].TagName.toLowerCase()] = ltt[i].Text;
    }
    /* Put the language tag text in the local storage and global variable (lngs) */
    localStorage.setItem(this.keyLanguageId, "" + languageId);
    localStorage.setItem(this.keyLanguageTagTexts, JSON.stringify(lng));
    this._languageTagTexts = lng;
  }

  languageData(result: any, id: string) {
    if (result.Error === '') {
      this.setLanguageTagTexts(result.LanguageTagTexts, id);
      localStorage.setItem(this.keyMenuItemsHierarchical, JSON.stringify(result.MenuHierarchical_diRect_Desktop));
      localStorage.setItem(this.keyMenuItemsFlat, JSON.stringify(result.MenuFlat_diRect_Desktop));
    }
  }

  companyData(id: string) {
    localStorage.setItem(this.keyCompanyId, JSON.stringify(id));
    this._companyId = id;
  }

  logout() {
    if(this.isAzureAuthenticated){
      this.authService.logout(); //Azure logout
      this.isAzureAuthenticated = false;
    }

    this.clearLocalStorage();
    this.zone.run(() => this.router.navigate(['/login']));
  }

  clearLocalStorage() {
    localStorage.removeItem(this.keyIsLoggedIn);
    localStorage.removeItem(this.keySessionId);
    localStorage.removeItem(this.keyUserId);
    localStorage.removeItem(this.keyAccount);
    localStorage.removeItem(this.keyMenuItemsHierarchical);
    localStorage.removeItem(this.keyMenuItemsFlat);
    localStorage.removeItem(this.keyLanguageTagTexts);
    localStorage.removeItem(this.keyCompanies);
    localStorage.removeItem(this.keyCompanyId);
    localStorage.removeItem(this.keyLanguages);
    localStorage.removeItem(this.keyLanguageId);
  }
}
